import { createSlice, isAnyOf, current } from "@reduxjs/toolkit";
import { meetingApi } from "@store/api/meeting";
import { agendaApi } from "@store/api/agenda";
import { getUserIdFromMeetingInstanceId } from "@utils/helpers";
import { getMeetingLiveActivityFromPayload } from "@store/utils/meetingPayload";

const initialState = {
  data: {
    inAMeeting: false,
    meetingObject: null,
    meetingActivities: null,
    meetingHost: null,
  },
  isFetchingMeeting: true,
  meetingHost: {
    host: null,
    coHosts: [],
  },
  ePulse: {
    data: [],
  },
  analytics: {
    live: false,
    data: [],
  },
};

// Hacky solution to fix issue with agenda when user joins or leaves meeting
// Should be removed when agenda, activity, and meeting slices are cleaned up
const generateNewMeetingPayload = (state, payload) => {
  const { meetingInstances } = payload;
  const {
    participantsInMeetingOnTH,
    participantsInMeetingOnZoom,
    participantsOpenedTH,
  } = meetingInstances[0];

  const oldMeetingInstance = state.data.meetingObject?.meetingInstances[0];

  // If the meeting IDs don't match (e.g., stale Zoom meeting vs new demo web meeting),
  // use the incoming payload directly instead of merging with stale state
  const isSameMeeting =
    oldMeetingInstance?._id === meetingInstances[0]?._id ||
    state.data.meetingObject?._id === payload._id;

  if (!oldMeetingInstance || !isSameMeeting) {
    return payload;
  }

  const newMeetingInstance = {
    ...oldMeetingInstance,
    participantsInMeetingOnTH,
    participantsInMeetingOnZoom,
    participantsOpenedTH,
  };

  const newMeetingObject = {
    ...payload,
    meetingInstances: [newMeetingInstance],
  };
  return newMeetingObject;
};

// TODO: organize data in meeting slice (meetingObject, meetingInstance etc);
const meetingSlice = createSlice({
  name: "meeting",
  initialState,
  reducers: {
    updateParticipantJoinedMeeting: (state, { payload }) => {
      console.log("[DEMO-DEBUG] updateParticipantJoinedMeeting", {
        participantCount:
          payload.meetingInstances?.[0]?.participantsInMeetingOnTH?.length,
        meetingId: payload._id,
        currentMeetingId: state.data.meetingObject?._id,
      });
      const liveActivity = getMeetingLiveActivityFromPayload(payload);
      state.data.inAMeeting = true;
      const newMeetingObject = generateNewMeetingPayload(
        current(state),
        payload
      );
      state.data.meetingObject = newMeetingObject;
      state.data.meetingActivities = liveActivity.type
        ? { [liveActivity.type]: liveActivity.data }
        : {};
      // For web meetings, meetingHost comes as a separate field
      // For Zoom meetings, host might be in participants list
      state.data.meetingHost =
        payload.meetingHost ||
        payload.meetingInstances[0].participantsInMeetingOnTH.find(
          (participant) => participant._id === payload.hostId
        );
    },
    updateParticipantLeft: (state, action) => {
      const { meetingObject, profileId } = action.payload;

      if (meetingObject?._id === state.data.meetingObject?._id) {
        const isHost = meetingObject.hostId === profileId;
        const isParticipant =
          meetingObject.meetingInstances[0].participantsInMeetingOnTH.find(
            (item) => item._id === profileId
          );

        const stillInMeeting = isHost || isParticipant;

        if (stillInMeeting) {
          const newMeetingObject = generateNewMeetingPayload(
            current(state),
            meetingObject
          );
          state.data.meetingObject = newMeetingObject;
        } else {
          return initialState;
        }
      }
    },
    updateLiveMeetingStatus: (state, action) => {
      state.analytics.live = action.payload;
    },
    updateAnalyticsData: (state, action) => {
      state.analytics.data.push({ uv: action.payload });
      if (state.analytics.data.length > 6) {
        state.analytics.data.shift();
      }
    },
    updateEPulseData: (state, action) => {
      state.ePulse.data = action.payload;
    },
    updateMeetingActivityStarted: (state, action) => {
      const { type, activity } = action.payload;
      const meetingActivities = state.data.meetingActivities || {};
      const updatedMeetingActivity = { ...meetingActivities, [type]: activity };
      state.data.meetingActivities = updatedMeetingActivity;
    },
    updateMeetingActivityEnded: (state, action) => {
      const { type, activityId } = action.payload;
      const activity = state.data.meetingActivities?.[type];
      if (activity && activity._id === activityId) {
        state.data.meetingActivities[type].live = false;
      }
    },
    clearMeetingState: () => ({ ...initialState, isFetchingMeeting: false }),
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      isAnyOf(
        meetingApi.endpoints.checkExistingMeeting.matchFulfilled,
        meetingApi.endpoints.joinMeetingFromWeb.matchFulfilled,
        meetingApi.endpoints.checkWebUserLiveMeeting.matchFulfilled
      ),
      (state, { payload, type }) => {
        console.log("[DEMO-DEBUG] meetingSlice checkExisting/join FULFILLED", {
          actionType: type,
          inAMeeting: state.data.inAMeeting,
          hasMeetingInstances: !!payload.meetingInstances?.length,
          currentMeetingId: state.data.meetingObject?._id,
          isFetchingMeeting: state.isFetchingMeeting,
        });

        // Don't let a background meeting check clobber an active meeting
        if (state.data.inAMeeting && !payload.meetingInstances?.length) {
          console.log(
            "[DEMO-DEBUG] meetingSlice: SKIPPING overwrite — active meeting protected"
          );
          state.isFetchingMeeting = false;
          return;
        }

        const { liveActivity = {} } = payload.meetingInstances[0]?.agenda || {};
        state.data.inAMeeting = !!payload.meetingInstances.length;
        state.data.meetingObject = payload;
        state.isFetchingMeeting = false;
        state.data.meetingActivities = liveActivity.type
          ? { [liveActivity?.type]: liveActivity?.data }
          : {};
        state.data.meetingHost = payload.meetingInstances.length
          ? payload.meetingInstances[0].participantsInMeetingOnTH.find(
              (participant) =>
                participant._id ===
                getUserIdFromMeetingInstanceId(payload.meetingInstances[0]._id)
            )
          : null;
        state.meetingHost =
          payload.meetingInstances?.[0]?.platformMeetingHosts ||
          initialState.meetingHost;
      }
    );
    builder.addMatcher(
      isAnyOf(
        meetingApi.endpoints.checkExistingMeeting.matchPending,
        meetingApi.endpoints.joinMeetingFromWeb.matchPending,
        meetingApi.endpoints.checkWebUserLiveMeeting.matchPending
      ),
      (state, { type }) => {
        console.log("[DEMO-DEBUG] meetingSlice checkExisting PENDING", {
          actionType: type,
          currentInAMeeting: state.data.inAMeeting,
        });
        state.isFetchingMeeting = true;
      }
    );
    builder.addMatcher(
      isAnyOf(
        meetingApi.endpoints.checkExistingMeeting.matchRejected,
        meetingApi.endpoints.joinMeetingFromWeb.matchRejected,
        meetingApi.endpoints.checkWebUserLiveMeeting.matchRejected
      ),
      (state, { type }) => {
        console.log("[DEMO-DEBUG] meetingSlice checkExisting REJECTED", {
          actionType: type,
        });
        state.isFetchingMeeting = false;
      }
    );
    builder.addMatcher(
      meetingApi.endpoints.leaveMeetingFromWeb.matchFulfilled,
      () => initialState
    );
    builder.addMatcher(
      agendaApi.endpoints.stopLiveAgenda.matchFulfilled,
      (state) => {
        state.ePulse.data = [];
      }
    );
    // TODO: Move all agenda endpoints and related data to Agenda Slice
    builder.addMatcher(
      isAnyOf(
        agendaApi.endpoints.goLiveAgenda.matchFulfilled,
        agendaApi.endpoints.instantStart.matchFulfilled
      ),
      (state, { payload }) => {
        state.data.meetingObject.meetingInstances[0].agenda = payload;
      }
    );
    builder.addMatcher(
      agendaApi.endpoints.stopLiveAgenda.matchFulfilled,
      (state) => {
        state.data.meetingObject.meetingInstances[0].agenda = null;
      }
    );

    // Handle web meeting start - use standardized meeting object from backend
    builder.addMatcher(
      meetingApi.endpoints.startWebMeeting.matchFulfilled,
      (state, { payload }) => {
        console.log("[DEMO-DEBUG] meetingSlice startWebMeeting FULFILLED", {
          meetingId: payload._id,
          platform: payload.platform,
          hasAgenda: !!payload.agenda,
          agendaLive: payload.agenda?.live,
        });
        state.data.inAMeeting = true;
        state.data.meetingObject = payload;
        state.data.meetingActivities = {};
        state.data.meetingHost = payload.meetingHost;
        state.isFetchingMeeting = false;
      }
    );

    // Handle demo meeting creation - DO NOT set inAMeeting here
    // Demo agenda creation should NOT trigger socket connection.
    // Socket should only connect when user clicks "Start Agenda" (startWebMeeting).
    // The demo page loads agenda data via RTK Query, not from meetingObject.
    builder.addMatcher(
      agendaApi.endpoints.createDemoAgenda.matchFulfilled,
      (state) => {
        // Only reset fetching state, don't set inAMeeting
        // Socket will connect when startWebMeeting is called
        state.isFetchingMeeting = false;
      }
    );

    // Handle web meeting join - use standardized meeting object from backend
    builder.addMatcher(
      meetingApi.endpoints.joinWebMeeting.matchFulfilled,
      (state, { payload }) => {
        // Backend now returns complete standardized meeting object directly
        const liveActivity = getMeetingLiveActivityFromPayload(payload);
        state.data.inAMeeting = true;
        state.data.meetingObject = payload;
        state.data.meetingActivities = liveActivity.type
          ? { [liveActivity.type]: liveActivity.data }
          : {};
        state.data.meetingHost = payload.meetingHost;
        state.isFetchingMeeting = false;
      }
    );

    // Handle web meeting leave - clear meeting data but don't reset to loading state
    builder.addMatcher(
      meetingApi.endpoints.leaveWebMeeting.matchFulfilled,
      () => ({ ...initialState, isFetchingMeeting: false })
    );

    // Handle web meeting stop - clear meeting data but don't reset to loading state
    builder.addMatcher(
      meetingApi.endpoints.stopWebMeeting.matchFulfilled,
      () => {
        console.log(
          "[DEMO-DEBUG] meetingSlice stopWebMeeting FULFILLED — clearing state, isFetchingMeeting=false"
        );
        return { ...initialState, isFetchingMeeting: false };
      }
    );
  },
});

const { reducer, actions } = meetingSlice;

export const {
  clearMeetingState,
  updateParticipantJoinedMeeting,
  updateParticipantLeft,
  updateLiveMeetingStatus,
  updateAnalyticsData,
  updateEPulseData,
  updateMeetingActivityStarted,
  updatePollAnswer,
  updateMeetingActivityEnded,
} = actions;
export default reducer;
